import { StateCreator } from 'zustand';
import { User, Department } from '../../types';
import { supabase } from '../../config/supabase';

export interface EmployeeSlice {
  availableUsers: User[];
  employees: Record<Department, User[]>;
  getDepartmentEmployees: (department: Department) => User[];
  addEmployee: (employee: User) => void;
  removeEmployee: (employeeId: string) => void;
  updateEmployee: (employee: User) => void;
  fetchEmployees: () => Promise<void>;
}

export const createEmployeeSlice: StateCreator<EmployeeSlice> = (set, get) => ({
  availableUsers: [],
  employees: {
    EXECUTIVE: [],
    SALES: [],
    PURCHASE: [],
    ACCOUNTS_RECEIVABLE: [],
    ACCOUNTS_PAYABLE: [],
    OPERATIONS: [],
    WAREHOUSE: []
  },
  
  getDepartmentEmployees: (department) => {
    return get().employees[department] || [];
  },
  
  addEmployee: (employee) => {
    set((state) => {
      // Add to availableUsers
      const newAvailableUsers = [...state.availableUsers.filter(u => u.id !== employee.id), employee];
      
      // Update employees by department
      const newEmployees = { ...state.employees };
      const dept = employee.department;

      // Remove from any other departments first
      Object.keys(newEmployees).forEach((d) => {
        newEmployees[d as Department] = newEmployees[d as Department]?.filter(e => e.id !== employee.id) || [];
      });

      // Add to correct department
      if (!newEmployees[dept]) {
        newEmployees[dept] = [];
      }
      newEmployees[dept] = [...newEmployees[dept].filter(e => e.id !== employee.id), employee];

      return {
        availableUsers: newAvailableUsers,
        employees: newEmployees
      };
    });
  },
  
  removeEmployee: (employeeId) => {
    set((state) => {
      // Remove from availableUsers
      const newAvailableUsers = state.availableUsers.filter(u => u.id !== employeeId);
      
      // Remove from departments
      const newEmployees = { ...state.employees };
      Object.keys(newEmployees).forEach((dept) => {
        newEmployees[dept as Department] = newEmployees[dept as Department]
          .filter(emp => emp.id !== employeeId);
      });

      return { 
        availableUsers: newAvailableUsers,
        employees: newEmployees
      };
    });
  },

  updateEmployee: (updatedEmployee) => {
    set((state) => {
      // Update in availableUsers
      const newAvailableUsers = state.availableUsers.map(user =>
        user.id === updatedEmployee.id ? updatedEmployee : user
      );
      
      // Update in departments
      const newEmployees = { ...state.employees };
      Object.keys(newEmployees).forEach((dept) => {
        if (dept === updatedEmployee.department) {
          newEmployees[dept as Department] = [
            ...newEmployees[dept as Department].filter(e => e.id !== updatedEmployee.id),
            updatedEmployee
          ];
        } else {
          newEmployees[dept as Department] = newEmployees[dept as Department]
            .filter(e => e.id !== updatedEmployee.id);
        }
      });

      return { 
        availableUsers: newAvailableUsers,
        employees: newEmployees
      };
    });
  },

  fetchEmployees: async () => {
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .order('name');

      if (error) throw error;

      const users = profiles.map(profile => ({
        id: profile.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        department: profile.department,
        avatar: profile.avatar || `https://api.dicebear.com/7.x/avatars/svg?seed=${profile.email}`,
        phone: profile.phone
      }));

      // Group by department
      const employeesByDept = users.reduce((acc, user) => {
        const dept = user.department;
        if (!acc[dept]) acc[dept] = [];
        acc[dept].push(user);
        return acc;
      }, {} as Record<Department, User[]>);

      set({ 
        availableUsers: users,
        employees: employeesByDept
      });
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      throw error;
    }
  }
});